import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getContent,
  updateContent,
  deleteContent,
  archiveContent,
  getAccessLogs,
  type AccessLog,
} from '../api'

type AccessType = 'logged_in' | 'phone_list' | 'admin_only' | 'public' | 'password'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

export default function ContentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: content, isLoading } = useQuery({
    queryKey: ['content', id],
    queryFn: () => getContent(id!),
    enabled: !!id,
  })

  const { data: logsData } = useQuery({
    queryKey: ['logs', id],
    queryFn: () => getAccessLogs(id!),
    enabled: !!id,
  })

  // Edit state
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editTags, setEditTags] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editAccess, setEditAccess] = useState<AccessType>('logged_in')
  const [editPhones, setEditPhones] = useState('')
  const [editExpiry, setEditExpiry] = useState('')

  const [copied, setCopied] = useState(false)
  const [saveError, setSaveError] = useState('')

  const updateMut = useMutation({
    mutationFn: (data: Parameters<typeof updateContent>[1]) =>
      updateContent(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', id] })
      setEditing(false)
      setSaveError('')
    },
    onError: (err: Error) => setSaveError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteContent(id!),
    onSuccess: () => navigate('/admin', { replace: true }),
  })

  const archiveMut = useMutation({
    mutationFn: () => archiveContent(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content', id] }),
  })

  function startEdit() {
    if (!content) return
    setEditTitle(content.title)
    setEditTags(content.tags.join(', '))
    setEditDesc(content.description)
    setEditAccess(content.permission?.access_type ?? 'logged_in')
    setEditPhones(content.permission?.phone_list.join(', ') ?? '')
    setEditExpiry(content.permission?.expires_at ?? '')
    setEditing(true)
  }

  function handleSave() {
    updateMut.mutate({
      title: editTitle,
      tags: editTags.split(',').map((t) => t.trim()).filter(Boolean),
      description: editDesc,
      permission: {
        access_type: editAccess,
        phone_list: editPhones.split(',').map((p) => p.trim()).filter(Boolean),
        expires_at: editExpiry || null,
      },
    })
  }

  function copyLink() {
    if (!content) return
    navigator.clipboard.writeText(`https://aibens.cc/v/${content.slug}`).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        内容不存在
      </div>
    )
  }

  const slug = content.slug
  const url = `https://aibens.cc/v/${slug}`
  const typeBadgeCls =
    content.type === 'html' ? 'badge-html' : content.type === 'pdf' ? 'badge-pdf' : 'badge-ppt'

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-gray-400 hover:text-gray-200 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div className="flex items-center gap-2">
              <span className={typeBadgeCls}>{content.type.toUpperCase()}</span>
              <h1 className="font-semibold text-white truncate max-w-xs">{content.title}</h1>
              {content.archived && (
                <span className="text-xs text-gray-500">[已归档]</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyLink} className="btn-secondary text-xs px-3 py-1.5">
              {copied ? '已复制链接' : '复制链接'}
            </button>
            <a href={url} target="_blank" rel="noreferrer" className="btn-secondary text-xs px-3 py-1.5">
              预览
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Metadata card */}
        <div className="card space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-gray-300 text-sm uppercase tracking-wider">内容信息</h2>
            {!editing && (
              <button onClick={startEdit} className="btn-secondary text-xs px-3 py-1.5">编辑</button>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">标题</label>
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="input" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">描述</label>
                <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
                  rows={2} className="input resize-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">标签（逗号分隔）</label>
                <input value={editTags} onChange={(e) => setEditTags(e.target.value)} className="input" />
              </div>

              {/* Permission editor */}
              <div className="border-t border-gray-800 pt-4 space-y-3">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">访问权限</p>
                <div className="flex gap-2 flex-wrap">
                  {([
                    { value: 'logged_in', label: '已登录用户' },
                    { value: 'public', label: '公开访问' },
                    { value: 'phone_list', label: '指定手机号' },
                    { value: 'admin_only', label: '仅管理员' },
                  ] as { value: AccessType; label: string }[]).map((opt) => (
                    <button key={opt.value} type="button"
                      onClick={() => setEditAccess(opt.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                        editAccess === opt.value
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-800 border-gray-700 text-gray-400'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {editAccess === 'phone_list' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">手机号列表</label>
                    <textarea value={editPhones} onChange={(e) => setEditPhones(e.target.value)}
                      rows={2} className="input resize-none text-sm"
                      placeholder="13800138000, 13900139000" />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">过期时间（留空为永不过期）</label>
                  <input type="datetime-local" value={editExpiry}
                    onChange={(e) => setEditExpiry(e.target.value)} className="input max-w-xs" />
                </div>
              </div>

              {saveError && (
                <p className="text-red-400 text-sm">{saveError}</p>
              )}

              <div className="flex gap-2 pt-2">
                <button onClick={() => { setEditing(false); setSaveError('') }}
                  className="btn-secondary flex-1">取消</button>
                <button onClick={handleSave} disabled={updateMut.isPending}
                  className="btn-primary flex-1">
                  {updateMut.isPending ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                { label: 'Slug', value: slug },
                { label: '类型', value: content.type.toUpperCase() },
                { label: '大小', value: formatBytes(content.size_bytes) },
                { label: '创建时间', value: formatDate(content.created_at) },
                { label: '更新时间', value: formatDate(content.updated_at) },
                {
                  label: '访问控制',
                  value:
                    content.permission?.access_type === 'logged_in' ? '已登录用户'
                    : content.permission?.access_type === 'phone_list' ? '指定手机号'
                    : content.permission?.access_type === 'admin_only' ? '仅管理员'
                    : content.permission?.access_type === 'public' ? '公开访问'
                    : content.permission?.access_type === 'password' ? '密码保护'
                    : '已登录用户',
                },
                {
                  label: '过期时间',
                  value: content.permission?.expires_at
                    ? formatDate(content.permission.expires_at)
                    : '永不过期',
                },
              ].map((item) => (
                <div key={item.label}>
                  <dt className="text-xs text-gray-500 mb-0.5">{item.label}</dt>
                  <dd className="text-sm text-gray-200">{item.value}</dd>
                </div>
              ))}

              {content.tags.length > 0 && (
                <div className="col-span-full">
                  <dt className="text-xs text-gray-500 mb-1">标签</dt>
                  <dd className="flex flex-wrap gap-1">
                    {content.tags.map((tag) => (
                      <span key={tag} className="text-xs text-gray-400 bg-gray-800 px-2 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </dd>
                </div>
              )}

              {content.description && (
                <div className="col-span-full">
                  <dt className="text-xs text-gray-500 mb-0.5">描述</dt>
                  <dd className="text-sm text-gray-300">{content.description}</dd>
                </div>
              )}

              {/* Access link */}
              <div className="col-span-full pt-2 border-t border-gray-800">
                <dt className="text-xs text-gray-500 mb-1">访问链接</dt>
                <dd className="flex gap-2">
                  <input readOnly value={url} className="input text-sm font-mono flex-1" />
                  <button onClick={copyLink} className="btn-primary px-3 shrink-0 text-sm">
                    {copied ? '已复制' : '复制'}
                  </button>
                </dd>
              </div>
            </dl>
          )}
        </div>

        {/* Danger zone */}
        <div className="card border-gray-800 space-y-3">
          <h2 className="font-medium text-gray-300 text-sm uppercase tracking-wider">操作</h2>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => archiveMut.mutate()}
              disabled={archiveMut.isPending}
              className="btn-secondary"
            >
              {content.archived ? '取消归档' : '归档'}
            </button>
            <button
              onClick={() => {
                if (window.confirm(`确认删除「${content.title}」？此操作不可撤销。`)) {
                  deleteMut.mutate()
                }
              }}
              disabled={deleteMut.isPending}
              className="btn-danger"
            >
              {deleteMut.isPending ? '删除中...' : '删除内容'}
            </button>
          </div>
        </div>

        {/* Access logs */}
        <div className="card space-y-4">
          <h2 className="font-medium text-gray-300 text-sm uppercase tracking-wider">
            访问记录
            {logsData?.logs.length ? (
              <span className="ml-2 text-gray-500 font-normal normal-case">
                最近 {logsData.logs.length} 条
              </span>
            ) : null}
          </h2>

          {!logsData?.logs.length ? (
            <p className="text-gray-500 text-sm">暂无访问记录</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-2 pr-4 text-gray-400 font-medium text-xs">手机号</th>
                    <th className="text-left py-2 pr-4 text-gray-400 font-medium text-xs">IP</th>
                    <th className="text-left py-2 pr-4 text-gray-400 font-medium text-xs hidden md:table-cell">User Agent</th>
                    <th className="text-left py-2 text-gray-400 font-medium text-xs">时间</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {(logsData.logs as AccessLog[]).map((log) => (
                    <tr key={log.id} className="hover:bg-gray-800/20">
                      <td className="py-2 pr-4 text-gray-300">{log.phone ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-400 font-mono text-xs">{log.ip ?? '—'}</td>
                      <td className="py-2 pr-4 text-gray-500 text-xs truncate max-w-xs hidden md:table-cell">
                        {log.user_agent ?? '—'}
                      </td>
                      <td className="py-2 text-gray-400 text-xs whitespace-nowrap">
                        {formatDate(log.accessed_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
